/**
 * SitesSidebar — lista plana "Todas + N sites" usada no split panel de
 * Admin → Ativos. Mais simples que DevicesSidebar porque o nível dos
 * cards direitos é o próprio equipment (não precisa árvore expansível).
 */

import clsx from "clsx";
import { Building2, Package } from "lucide-react";

import type { Equipment, Site } from "@/types";

export type SelectedSiteNode =
  | { kind: "all" }
  | { kind: "site"; id: string };

interface Props {
  sites: Site[];
  equipments: Equipment[];  // pra contar por site
  selected: SelectedSiteNode;
  onSelect: (node: SelectedSiteNode) => void;
}

export function SitesSidebar({ sites, equipments, selected, onSelect }: Props) {
  // Conta equipments por site (só ativos → total; contagem separada de
  // arquivados fica pro tooltip se der; simplificando agora, count = total)
  const countBySite = new Map<string, number>();
  for (const e of equipments) {
    countBySite.set(e.site_id, (countBySite.get(e.site_id) ?? 0) + 1);
  }

  return (
    <nav className="space-y-1 text-sm" aria-label="Sites">
      <NodeButton
        label="Todos os ativos"
        icon={<Package className="h-4 w-4" />}
        count={equipments.length}
        active={selected.kind === "all"}
        onClick={() => onSelect({ kind: "all" })}
      />

      {sites.map((site) => {
        const count = countBySite.get(site.id) ?? 0;
        const isActive = selected.kind === "site" && selected.id === site.id;
        return (
          <NodeButton
            key={site.id}
            label={site.name}
            icon={<Building2 className="h-4 w-4 text-brand-600 dark:text-brand-400" />}
            count={count}
            active={isActive}
            onClick={() => onSelect({ kind: "site", id: site.id })}
          />
        );
      })}
    </nav>
  );
}

function NodeButton({
  label, icon, count, active, onClick,
}: {
  label: string;
  icon: React.ReactNode;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left",
        active
          ? "bg-brand-100/70 dark:bg-brand-900/40 font-semibold text-brand-900 dark:text-brand-100"
          : "text-slate-700 dark:text-slate-300 hover:bg-surface-100/70 dark:hover:bg-slate-800/60",
      )}
    >
      {icon}
      <span className="flex-1 truncate">{label}</span>
      <span className="tabular-nums text-[11px] text-slate-500 dark:text-slate-400">
        {count}
      </span>
    </button>
  );
}
